/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or
 * distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly
 * prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this
 * content, please contact the author for proper authorization and agreement.
 */

import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppDispatch } from 'helpers/store'
import { openSecondaryModal, closeSecondaryModal, closeModal } from 'helpers/actions'
import { ModalSize } from 'types/enums/ModalSize'
import { Company } from 'types/entities/Company'
import { Lead } from 'types/entities/Lead'
import { Offer } from 'types/entities/Offer'
import { EditCompanyModalTab } from 'types/enums/specific/EditCompanyModalTab'
import BookingAction from 'store/booking/BookingAction'
import { OffersAction } from 'store/offers/OffersAction'
import { setDraftOfferId } from 'store/offers/OffersReducer'
import { upsertSelectedCompanyOffer } from 'store/companies/CompaniesReducer'
import LeadsAction from 'store/leads/LeadsAction'
import { toast } from 'utils/toastUtils'
import CreateLeadModal from 'components/leads/CreateLeadModal'
import InProgressOfferGuardModal from 'components/Offers/InProgressOfferGuardModal'
import EditLeadModal from 'components/leads/EditLeadModal'
import EditOfferModal from 'components/Offers/EditOfferModal'
import ContactCompanyDetail from 'pages/Front/Contacts/ContactCompanyDetail'
import { CompanyContact } from 'types/entities/CompanyContact'

interface UseEditCompanyActionsParams {
    selectedCompany: Company | null
    isSecondary?: boolean
    onSuccess?: (company: Company) => void
    refreshSelectedCompany: () => Promise<void | boolean>
}

export const useEditCompanyActions = ({
    selectedCompany,
    isSecondary,
    onSuccess,
    refreshSelectedCompany,
}: UseEditCompanyActionsParams) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const createOfferInProgressIdRef = useRef<number | null>(null)
    const [activeTab, setActiveTab] = useState<EditCompanyModalTab>(EditCompanyModalTab.Company)

    const doTabSwitch = (tabId: EditCompanyModalTab) => {
        setActiveTab(tabId)
        if (tabId === EditCompanyModalTab.CreateOffer) {
            dispatch(OffersAction.resetSelectedOffer())
            dispatch(BookingAction.clearBookingProcessState())
        }
    }

    const handleTabClick = (tabId: EditCompanyModalTab) => {
        const leavingWizard =
            activeTab === EditCompanyModalTab.CreateOffer &&
            tabId !== EditCompanyModalTab.CreateOffer &&
            createOfferInProgressIdRef.current !== null

        if (leavingWizard) {
            const offerId = createOfferInProgressIdRef.current
            dispatch(
                openSecondaryModal(
                    <InProgressOfferGuardModal
                        offerId={offerId}
                        onDeleteAndLeave={async () => {
                            await dispatch(OffersAction.fetchDeleteOffer(offerId))
                            dispatch(setDraftOfferId(null))
                            createOfferInProgressIdRef.current = null
                            dispatch(closeSecondaryModal())
                            doTabSwitch(tabId)
                        }}
                        onKeepAndLeave={() => {
                            dispatch(setDraftOfferId(offerId))
                            createOfferInProgressIdRef.current = null
                            dispatch(closeSecondaryModal())
                            doTabSwitch(tabId)
                        }}
                        onStay={() => dispatch(closeSecondaryModal())}
                    />,
                    { modalSize: ModalSize.Small },
                    t('Leave Offer Wizard?')
                )
            )
            return
        }

        doTabSwitch(tabId)
    }

    const handleCreateLead = () => {
        if (!selectedCompany) {
            toast.error(t('Failed to load company details'))
            return
        }
        dispatch(
            openSecondaryModal(
                <CreateLeadModal
                    onSuccess={() => { refreshSelectedCompany() }}
                    company={selectedCompany}
                    isSecondary={true}
                    disableCompanyChange={true}
                />,
                {
                    modalSize: ModalSize.Small,
                    closeModal: () => dispatch(closeSecondaryModal()),
                    onError: () => toast.error(t('An error occurred')),
                },
                t('Create Lead')
            )
        )
    }

    const handleCreateOffer = () => {
        if (!selectedCompany) {
            toast.error(t('Failed to load company details'))
            return
        }
        dispatch(OffersAction.resetSelectedOffer())
        dispatch(BookingAction.clearBookingProcessState())
        setActiveTab(EditCompanyModalTab.CreateOffer)
    }

    const handleLeadClick = (lead: Lead) => {
        dispatch(LeadsAction.fetchLeadById(lead))
            .then((loadedLead: Lead) =>
                dispatch(
                    openSecondaryModal(
                        <EditLeadModal
                            lead={loadedLead}
                            onSuccess={() => { refreshSelectedCompany() }}
                            isSecondary={true}
                        />,
                        {
                            modalSize: ModalSize.RoundedFullscreen,
                            closeModal: () => dispatch(closeSecondaryModal()),
                        },
                        t('Edit Lead')
                    )
                )
            )
            .catch(() => toast.error(t('Failed to load lead information')))
    }

    const handleOfferClick = (offer: Offer) => {
        dispatch(
            openSecondaryModal(
                <EditOfferModal offerId={offer.id} />,
                {
                    modalSize: ModalSize.RoundedFullscreen,
                    closeModal: () => dispatch(closeSecondaryModal()),
                },
                t('Offer Detail')
            )
        )
    }

    const handleViewContact = (contact: CompanyContact) => {
        dispatch(
            openSecondaryModal(
                <ContactCompanyDetail contactId={contact.id} />,
                { modalSize: ModalSize.RoundedFullscreen },
                t('Contact Details')
            )
        )
    }

    const onChangeCompanyHandler = (shouldClose: boolean = false) => {
        if (!shouldClose || !selectedCompany) return
        if (isSecondary) {
            dispatch(closeSecondaryModal())
        } else {
            dispatch(closeModal())
        }
        onSuccess?.(selectedCompany)
    }

    const handleOfferCreated = (offer?: Offer) => {
        createOfferInProgressIdRef.current = null
        if (offer) {
            dispatch(upsertSelectedCompanyOffer({ offer }))
        }
        setActiveTab(EditCompanyModalTab.Offers)
    }

    const handleOfferGuardStateChange = (id: number | null) => {
        createOfferInProgressIdRef.current = id
    }

    return {
        activeTab,
        handleTabClick,
        handleCreateLead,
        handleCreateOffer,
        handleLeadClick,
        handleOfferClick,
        handleViewContact,
        onChangeCompanyHandler,
        handleOfferCreated,
        handleOfferGuardStateChange,
    }
}
